import type { PositionBracket } from '@flipster/apis/v2/snapshot';
import { estimateOrderLiqPrice, type OrderInput } from '@flipster/features/src/shared/liquidation';
import type { Position } from '@flipster/r230522/original/position';
import Num, { BigNumber } from '@flipster/utils/num';
import type { OrderMarginType, V2OrderSideType } from '@flipster/utils/types/base';

export interface LiquidationPreviewInputs {
  symbolInfo: { quoteCurrency: string; unitOrderQty?: string | null; tickSize: string } | null | undefined;
  marginType: OrderMarginType;
  targetSymbol: string;
  askPrice: string;
  bidPrice: string;
  askQty: string;
  bidQty: string;
  markPrice: string;
  targetPosition: Position | null;
  margins: Record<string, { marginBalance?: string; maintPositionMargin?: string } | undefined>;
  pswap: { takerFeeRate: string; makerFeeRate: string } | undefined;
  positionBracket: PositionBracket | null | undefined;
  leverage: number;
}

export interface LiquidationPreview {
  long: string | null;
  short: string | null;
}

/**
 * 주문 체결 후(post-fill) 예상 청산가를 매수(Long)/매도(Short) 각각 산출한다.
 * 실제 청산 계산은 estimateOrderLiqPrice(parity 테스트 有)에 위임하고, 이 함수는 폼 입력을
 * 그쪽 인자로 조립하는 순수 로직만 담당한다:
 * - symbolInfo/pswap/positionBracket 중 하나라도 없으면 계산 불가 → { long: null, short: null }
 * - feeRate = max(taker, maker)
 * - 수량 미입력(0)이면 tier 1을 보장하는 unitOrderQty(없으면 '1')로 미리보기
 * - crossMarginBalance는 marginBalance(없으면 '0'), Long은 askPrice/askQty·Short는 bidPrice/bidQty 사용
 */
export function computeLiquidationPreview(inputs: LiquidationPreviewInputs): LiquidationPreview {
  const {
    symbolInfo,
    marginType,
    targetSymbol,
    askPrice,
    bidPrice,
    askQty,
    bidQty,
    markPrice,
    targetPosition,
    margins,
    pswap,
    positionBracket,
    leverage,
  } = inputs;

  if (!symbolInfo || !pswap || !positionBracket) return { long: null, short: null };

  const margin = margins[symbolInfo.quoteCurrency];
  // Liq calc needs the cross margin BALANCE (wallet + cross uPnL − reserved-for-isolated),
  // not `crossMarginAvailable` (= balance − reserved init margin), which would inflate liq.
  const crossMarginBalance = margin?.marginBalance ?? '0';
  const maintPositionMargin = margin?.maintPositionMargin;

  const feeRate = BigNumber.max(pswap.takerFeeRate, pswap.makerFeeRate).toString();
  // Show a stable preview when the user hasn't entered a size yet — fall back to one lot,
  // which is guaranteed to land in tier 1. Once the user types, the real qty walks brackets.
  const fallbackQty = symbolInfo.unitOrderQty || '1';
  const previewQty = (qty: string) => (Num(qty).gt(0) ? qty : fallbackQty);

  const buildOrder = (side: V2OrderSideType, price: string, qty: string): OrderInput => ({
    side,
    qty: previewQty(qty),
    price,
    initLeverage: leverage,
    feeRate,
    marginMode: marginType,
  });

  const sharedArgs = {
    targetPosition,
    markPrice,
    targetSymbol,
    crossMarginBalance,
    maintPositionMargin,
    tickSize: symbolInfo.tickSize,
    positionBracket,
  };

  const long = estimateOrderLiqPrice({ ...sharedArgs, order: buildOrder('Long', askPrice, askQty) });
  const short = estimateOrderLiqPrice({ ...sharedArgs, order: buildOrder('Short', bidPrice, bidQty) });

  return { long, short };
}
